import * as turf from "@turf/turf";
import type { Geometry } from "geojson";

// --- Cálculo (sem arredondamento) ---

export function computeAreaKm2(geometry: Geometry): number {
  const areaM2 = turf.area({ type: "Feature", geometry, properties: {} });
  return areaM2 / 1_000_000;
  // Sem Math.round: o número mantém precisão total do turf.
  // O arredondamento visual acontece apenas nas funções format* abaixo (máscara).
}

// --- Máscaras de exibição ---

// Valores abaixo deste limiar são exibidos como "0,001" — evita "0,000".
const MIN_DISPLAYABLE = 0.001;

export function formatAreaNumber(value: number, unit: string): string {
  const displayValue = value > 0 && value < MIN_DISPLAYABLE ? MIN_DISPLAYABLE : value;
  // Números < 1 começam com "0,..." → 3 casas decimais.
  // Demais → 2 casas fixas (ex: 8.280,30 m²).
  const decimals = displayValue < 1 ? 3 : 2;
  return displayValue.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatAreaByUnit(value: number, unit: string): string {
  return `${formatAreaNumber(value, unit)} ${unit}`;
}

export function formatAreaKm2(km2: number): string {
  return formatAreaByUnit(km2, "km²");
}

export const AREA_THRESHOLD_M2 = {
  SQ_KM: 1_000_000,
  HECTARE: 10_000,
} as const;

// Constantes internas derivadas — nunca mais divergem do ToolsHandler
const KM2_PER_HECTARE = AREA_THRESHOLD_M2.HECTARE / AREA_THRESHOLD_M2.SQ_KM; // 0.01
const HA_PER_KM2 = AREA_THRESHOLD_M2.SQ_KM / AREA_THRESHOLD_M2.HECTARE;      // 100
const M2_PER_KM2 = AREA_THRESHOLD_M2.SQ_KM;                                   // 1_000_000

export function formatAreaAdaptive(km2: number): string {
  if (km2 >= 1) {
    return formatAreaByUnit(km2, "km²");
  }
  if (km2 >= KM2_PER_HECTARE) {
    // Converte km² → ha com o valor real (não arredondado) antes de aplicar a máscara
    return formatAreaByUnit(km2 * HA_PER_KM2, "ha");
  }
  return formatAreaByUnit(km2 * M2_PER_KM2, "m²");
}

// --- Patch de cards do backend ---

// Label usado pelo backend para o card de área total — compartilhado com
// hasZeroStats (src/Components/Statistics/utils/areaStats.ts) para que os
// dois lugares que dependem desse texto fiquem acoplados a uma única fonte.
export const AREA_TOTAL_LABEL = "Área Total";

export function patchAreaCards<T extends { label: string; value: string }>(
  cards: T[],
  drawingAreaKm2: number
): T[] {
  const parsePtBR = (value: string): number =>
    parseFloat(value.replace(/\./g, "").replace(",", "."));

  // Sempre usa o valor turf como fonte de verdade — elimina divergência entre painéis.
  const turfFormatted = formatAreaKm2(drawingAreaKm2);

  return cards.map((card) => {
    if (card.label === AREA_TOTAL_LABEL)
      return { ...card, value: turfFormatted };
    if (card.label === "Área Propícia") {
      const propNum = parsePtBR(card.value);
      if (!isNaN(propNum) && propNum > drawingAreaKm2)
        return { ...card, value: turfFormatted };
    }
    return card;
  });
}
